import Header from '~/components/route/Header';
import { Button } from '~/components/ui/button';
import { Form, Link, useActionData, useLoaderData } from '@remix-run/react';
import { ActionFunctionArgs, json, LoaderFunctionArgs, MetaFunction, redirect } from '@remix-run/node';
import { commitSession, getSession } from '~/sessions';
import appleSignInButton from '~/assets/apple_signin_button.png';
import { ANALYTICS_EVENTS, AUTH_ACTION, LOGIN, NOTIFICATION, TOKEN } from '~/constants/cookies';
import { SubmissionResult, useForm } from '@conform-to/react';
import { parseWithZod } from '@conform-to/zod';
import TermsAndPrivacyPolicy from '~/components/auth/TermsAndPrivacyPolicy';
import MethodDivider from '~/components/auth/MethodDivider';
import { getLoginSchema } from '~/schemas/getLoginSchema';
import useIsLoading from '~/hooks/useIsLoading';
import NavBar from '~/components/navBar/NavBar';
import { PageRoutes } from '~/constants/routes';
import AuthEmailField from '~/components/auth/AuthEmailField';
import AuthPasswordField from '~/components/auth/AuthPasswordField';
import ErrorMessage from '~/components/formFields/ErrorMessage';
import Notification from '~/components/notifications/Notification';
import { handleGoogleOAuthRequest } from '~/utils/googleOAuth';
import { handleAppleOAuthRequest } from '~/utils/appleOAuth';
import { handleForgotPassword } from '~/utils/handleForgotPassword';
import { handleAuthWithPassword } from '~/utils/handleAuthWithPassword';
import { handleCheckEmail } from '~/utils/handleCheckEmail';
import GoogleSignInIcon from '~/components/icons/GoogleSignInIcon';
import useAnalyticsEvents from '~/hooks/useAnalyticsEvents';
import { ACCOUNT_DELETED_SEARCH_PARAM } from '~/constants/params';
import { NotificationType } from '~/constants/notification';

export const meta: MetaFunction = () => {
  return [{ title: 'Login | Custody Navigator' }];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const session = await getSession(request);
  const token = session.get(TOKEN);
  if (token) {
    return redirect(PageRoutes.ACCOUNT, {
      headers: {
        'Set-Cookie': await commitSession(session),
      },
    });
  }

  const url = new URL(request.url);
  const isAccountDeleted = url.searchParams.get(ACCOUNT_DELETED_SEARCH_PARAM);
  const notification = isAccountDeleted ? NotificationType.ACCOUNT_DELETED_SUCCESS : session.get(NOTIFICATION) || null;
  const analyticsEvents = session.get(ANALYTICS_EVENTS) || [];

  return json(
    {
      analyticsEvents,
      notification,
    },
    {
      headers: {
        'Set-Cookie': await commitSession(session),
      },
    }
  );
}

export async function action({ request }: ActionFunctionArgs) {
  const session = await getSession(request);
  const formData = await request.formData();
  const intent = formData.get('intent');
  const url = new URL(request.url);
  session.set(AUTH_ACTION, LOGIN);

  if (intent === 'apple') return handleAppleOAuthRequest(session, url);
  if (intent === 'google') return handleGoogleOAuthRequest(session, url);

  const email = formData.get('email') as string;
  const password = formData.get('password') as string;

  /** Has a password, clicked forgot password, sending OTP */
  if (intent === 'forgotPassword') return handleForgotPassword(session, email);
  /* Auth via email and password */
  if (email && password) return handleAuthWithPassword({ email, password, session });

  /* Initial email check: if no password on file, fire OTP; otherwise, show password field */
  return handleCheckEmail(email, session);
}

export default function Login() {
  const isLoading = useIsLoading();
  const { analyticsEvents, notification } = useLoaderData<typeof loader>();
  const lastResult = useActionData<SubmissionResult<typeof action> & { authError?: string; pwdOnFile?: boolean }>();
  const isShowPassword = Boolean(lastResult?.pwdOnFile);
  const [form, fields] = useForm({
    lastResult,

    onValidate({ formData, submitter }) {
      const isOAuthClick = submitter?.id === 'googleButton' || submitter?.id === 'appleButton';
      const intent = formData.get('intent');
      const isEmailRequired = isOAuthClick ? false : intent === 'email';
      const isPasswordRequired = isOAuthClick ? false : isShowPassword && intent !== 'forgotPassword';
      return parseWithZod(formData, { schema: getLoginSchema({ isEmailRequired, isPasswordRequired }) });
    },

    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput',
  });
  useAnalyticsEvents({ analyticsEvents });

  return (
    <div className="flex min-h-screen flex-col bg-neutral-offWhite">
      <NavBar isAuthenticated={false} onLogout={() => {}} userData={{ email: '', scheduleExists: false, name: '' }} />
      <div className="mb-24 px-4 sm:px-0">
        <div className="mx-auto my-0 flex max-w-96 flex-col bg-neutral-offWhite py-4 screen-md:max-w-[852px]">
          <Header title="Welcome back" />

          <Form method="POST" id={form.id} onSubmit={form.onSubmit} noValidate>
            <button className="hidden" type="submit" name="intent" value="email" />
            <div className="flex items-center justify-center py-8">
              <Button
                className="flex h-16 w-[360px] items-center justify-center bg-[#131314]"
                disabled={isLoading}
                id="googleButton"
                name="intent"
                type="submit"
                value="google"
              >
                <GoogleSignInIcon />
              </Button>
            </div>

            <div className="flex items-center justify-center pb-8">
              <Button
                className="flex h-16 w-[360px] items-center justify-center p-0"
                disabled={isLoading}
                id="appleButton"
                name="intent"
                type="submit"
                value="apple"
              >
                <picture className="flex h-full w-full">
                  <img src={appleSignInButton} alt="" loading="eager" width={360} height={64} />
                </picture>
              </Button>
            </div>

            <MethodDivider />

            <div className="flex items-center justify-center py-8">
              <div className="relative flex w-full flex-col items-center justify-center">
                <div className="flex w-full max-w-[360px] flex-col items-center px-1">
                  <AuthEmailField id={fields?.email?.id} name={fields?.email?.name} errors={fields?.email?.errors} />
                  {isShowPassword ? (
                    <AuthPasswordField
                      id={fields?.password.id}
                      name={fields?.password.name}
                      errors={fields?.password?.errors}
                    />
                  ) : null}
                </div>
                <Button
                  className="flex h-14 w-full max-w-[360px] items-center justify-center bg-green-dark text-base font-semibold text-neutral-offWhite"
                  type="submit"
                  name="intent"
                  value="email"
                  disabled={isLoading}
                >
                  Continue
                </Button>
                {lastResult?.authError ? <ErrorMessage error={lastResult.authError} /> : null}
              </div>
            </div>

            <TermsAndPrivacyPolicy />

            <div className="flex items-center justify-center py-8">
              {isShowPassword ? (
                <button name="intent" value="forgotPassword" className="text-sm font-medium text-green-dark underline">
                  Forgot your password?
                </button>
              ) : (
                <p className="text-center text-sm font-light text-brown-dark">
                  Don&apos;t have an account?{' '}
                  <Link to="/signup" className="font-bold text-green-dark">
                    Sign up.
                  </Link>
                </p>
              )}
            </div>
          </Form>
        </div>
      </div>
      <Notification notification={notification} />
    </div>
  );
}
